import { FAMILIES, type Family } from '@lpa/exercises/identity';
import {
  contrast,
  DEFAULT_CORE,
  hexToHsl,
  hslToHex,
  hueDistance,
  shadeForContrast,
  type ThemeName,
  type ThemePalette,
} from '@lpa/protocol';

/**
 * Keeping the eight exercise families distinguishable from the brand colour
 * (GAME-TYPES.md §1.4, D-30).
 *
 * The families use hue to group twenty-nine exercises, and the white label lets
 * a school choose a hue for everything else. Those two facts collide: a school
 * whose brand is a mid blue gets a `nouns` chip, an accent, a focus ring and a
 * selected tab that are all the same blue, and a colour that meant "this is a
 * noun exercise" now also means "this is selected". Colour is only ever the
 * third channel here -- the code identifies and the icon groups (§1.1) -- so
 * nothing becomes *unusable*. It becomes misleading, which is worse than
 * useless.
 *
 * So a family whose hue lands too near the core is moved. Which one moves is
 * decided by measurement, and where it moves to is decided by looking for the
 * emptiest part of the wheel -- not by a second hand-written table that would
 * have to be re-tuned for every client.
 */

export interface FamilyColors {
  /** Chip background. */
  tint: string;
  /** Text on that chip. */
  ink: string;
  /** The icon, and the board dot. */
  mark: string;
}

/**
 * The shipped family colours -- `apps/web/src/styles/families.css`, verbatim.
 *
 * Repeated here because the reassignment has to start from *something*, and
 * because a hue can only be compared against the core if it is a number rather
 * than a line in a stylesheet. `apps/web/test/branding.test.ts` parses the CSS and asserts
 * the two agree, in both themes, so the copy cannot drift from the file that
 * actually paints an unbranded install.
 */
export const DEFAULT_FAMILY_COLORS: Readonly<
  Record<ThemeName, Readonly<Record<Family, FamilyColors>>>
> = {
  light: {
    pronouns: { tint: '#ffebea', ink: '#7f1f29', mark: '#b23641' },
    'numbers-time': { tint: '#ffeddc', ink: '#693b00', mark: '#965800' },
    prepositions: { tint: '#f1f3d8', ink: '#4b4b00', mark: '#6e6e00' },
    verbs: { tint: '#dff8e6', ink: '#00572e', mark: '#007e46' },
    sound: { tint: '#d6f8f9', ink: '#005356', mark: '#00787d' },
    nouns: { tint: '#e4f2ff', ink: '#004b7b', mark: '#006eaf' },
    syntax: { tint: '#efeeff', ink: '#493687', mark: '#6a53bd' },
    adjectives: { tint: '#ffe8f9', ink: '#6f2563', mark: '#9d3d8d' },
  },
  dark: {
    pronouns: { tint: '#422424', ink: '#ffbebd', mark: '#ff9495' },
    'numbers-time': { tint: '#3d2914', ink: '#fbc592', mark: '#f0a556' },
    prepositions: { tint: '#2f3014', ink: '#d4d791', mark: '#bdbe53' },
    verbs: { tint: '#1a3424', ink: '#a1e3b7', mark: '#6cd092' },
    sound: { tint: '#0a3537', ink: '#85e3e8', mark: '#17d0d8' },
    nouns: { tint: '#193043', ink: '#a9d7ff', mark: '#71bfff' },
    syntax: { tint: '#2d2a43', ink: '#d0c9ff', mark: '#b6aaff' },
    adjectives: { tint: '#3c2537', ink: '#f7bbe9', mark: '#ea97d8' },
  },
};

/**
 * How near the core a family's hue may sit before it is moved, in degrees.
 *
 * The eight families are authored 45 degrees apart (§1.3), so 24 is a little
 * over half a gap: near enough that "is that the brand colour or the nouns
 * colour?" is a real question, far enough that at most two families can ever
 * be caught by one core -- which is what keeps the reassignment from cascading
 * through the whole set.
 */
export const CLASH_DEGREES = 24;

/**
 * Below this saturation a colour has no meaningful hue.
 *
 * A near-grey brand colour is 0 degrees from everything and 180 degrees from
 * everything, depending on rounding. Rather than let that reassign families at
 * random, a desaturated core clashes with nothing: it does not compete with
 * eight saturated hues in the first place.
 */
const MIN_SATURATION = 0.18;

/** Contrast a chip's icon must clear against the surface it sits on (§1.4). */
const MARK_CONTRAST = 3;

/** Contrast a chip's text must clear against its own tint. */
const INK_CONTRAST = 4.5;

/**
 * Which families collide with this core colour, and the hue each moves to.
 *
 * Empty when nothing collides -- which is the case for the shipped palette and
 * for most brand colours, since a clash needs a hue within `CLASH_DEGREES` of
 * one of eight.
 *
 * The replacement hue is whichever candidate is furthest from *everything* that
 * matters: the core, and every family that is staying put (plus the ones
 * already reassigned in this pass, so two clashing families cannot both be
 * moved on top of each other). Scanning in five-degree steps rather than
 * solving it: the search space is 72 candidates, the objective is a `min` over
 * nine numbers, and an exhaustive scan is both exact and obviously right.
 */
export function reassignedHues(core: string): Map<Family, number> {
  const moved = new Map<Family, number>();

  /*
   * The shipped set is exempt, and has to be.
   *
   * The app's own accent is a mid blue at 207 degrees and the `nouns` family
   * is a mid blue at 202 -- five degrees apart, well inside the threshold. That
   * is not an oversight to be corrected: the two were authored together and
   * checked together (§1.4), and they read as different things because the
   * accent is a fill and a family colour is a chip carrying an icon and a code.
   * Running the reassignment over the default would repaint `nouns` green on
   * every unbranded install, which is not a white label doing its job; it is a
   * white label changing an app nobody asked it to change.
   *
   * A colour somebody actually chose gets no such benefit of the doubt, because
   * nobody checked it against these eight.
   */
  if (core === DEFAULT_CORE) return moved;

  const coreHsl = hexToHsl(core);
  if (coreHsl.s < MIN_SATURATION) return moved;

  // Hues are read off the light-theme mark. The dark mark is the same hue with
  // the lightness rolled the other way, so either would answer the same.
  const hueOf = (family: Family): number => hexToHsl(DEFAULT_FAMILY_COLORS.light[family].mark).h;

  const clashing = FAMILIES.filter(
    (family) => hueDistance(hueOf(family), coreHsl.h) < CLASH_DEGREES,
  );
  if (clashing.length === 0) return moved;

  // Everything the replacement has to stay away from: the core, and the
  // families that are not moving.
  const occupied = [coreHsl.h, ...FAMILIES.filter((f) => !clashing.includes(f)).map(hueOf)];

  for (const family of clashing) {
    let best = coreHsl.h;
    let bestGap = -1;

    for (let candidate = 0; candidate < 360; candidate += 5) {
      const gap = Math.min(...occupied.map((h) => hueDistance(candidate, h)));
      if (gap > bestGap) {
        bestGap = gap;
        best = candidate;
      }
    }

    moved.set(family, best);
    // Claimed, so the next clashing family is pushed elsewhere.
    occupied.push(best);
  }

  return moved;
}

/**
 * A family's trio re-cut at a new hue, still readable on the branded palette.
 *
 * The saturation and lightness of the shipped trio are kept and only the hue
 * moves, so a reassigned family still looks like a member of the set rather
 * than a colour from a different app. The two contrast floors are then re-met
 * against the palette actually in force -- which may itself be branded, so
 * they cannot be assumed from the values that were computed against the
 * shipped surfaces.
 */
export function recolorFamily(
  family: Family,
  hue: number,
  theme: ThemeName,
  palette: ThemePalette,
): FamilyColors {
  const base = DEFAULT_FAMILY_COLORS[theme][family];

  const at = (hex: string): string => hslToHex({ ...hexToHsl(hex), h: hue });

  const tint = at(base.tint);
  const mark = shadeForContrast(at(base.mark), palette.surfaceRaised, MARK_CONTRAST);
  const ink = shadeForContrast(at(base.ink), tint, INK_CONTRAST);

  return { tint, ink, mark };
}

export interface FamilyClash {
  family: Family;
  /** The hue it was moved to, in degrees. */
  hue: number;
}

/**
 * Every family this core displaces, with the colours that replace it.
 *
 * The shape the stylesheet writer wants: one pass, both themes, and nothing at
 * all when the brand colour sits clear of all eight.
 */
export function resolveFamilyOverrides(
  core: string,
  palettes: Readonly<Record<ThemeName, ThemePalette>>,
): { clashes: FamilyClash[]; colors: Record<ThemeName, Partial<Record<Family, FamilyColors>>> } {
  const moved = reassignedHues(core);

  const colors: Record<ThemeName, Partial<Record<Family, FamilyColors>>> = { light: {}, dark: {} };
  const clashes: FamilyClash[] = [];

  for (const [family, hue] of moved) {
    clashes.push({ family, hue });
    colors.light[family] = recolorFamily(family, hue, 'light', palettes.light);
    colors.dark[family] = recolorFamily(family, hue, 'dark', palettes.dark);
  }

  return { clashes, colors };
}

/** Whether a resolved trio still meets the floors §1.4 sets. Used by the tests. */
export function familyContrastOk(colors: FamilyColors, palette: ThemePalette): boolean {
  return (
    contrast(colors.mark, palette.surfaceRaised) >= MARK_CONTRAST - 0.005 &&
    contrast(colors.ink, colors.tint) >= INK_CONTRAST - 0.005
  );
}
